import React, { useState, useRef, useEffect } from 'react';
import {
  Send, Paperclip, X, Image, FileSpreadsheet,
  Loader2, Bot, User, Code2, Trash2
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import CodeReviewPanel from './CodeReviewPanel';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  attachment?: { name: string; type: string };
  generatedCode?: GeneratedTransform | null;
}

interface GeneratedTransform {
  fn_name: string;
  code: string;
  params_schema: Record<string, string>;
  explanation: string;
  estimated_match_rates: Record<string, string>;
  warnings: string[];
}

interface CustomTransformChatProps {
  sessionId: number | null;
  engagementId: string;
  dfPath: string;
  dfSchema: Record<string, any>;
  dfSample: any[];
  rowCount: number;
  fileName: string;
  onTransformApplied?: (result: any) => void;
}

const CustomTransformChat: React.FC<CustomTransformChatProps> = ({
  sessionId, engagementId, dfPath, dfSchema: initialSchema, dfSample: initialSample, rowCount: initialRowCount, fileName, onTransformApplied,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [reviewingTransform, setReviewingTransform] = useState<GeneratedTransform | null>(null);
  const [reviewPromptSummary, setReviewPromptSummary] = useState('');
  const [liveSchema, setLiveSchema] = useState<Record<string, any>>(initialSchema || {});
  const [liveSample, setLiveSample] = useState<any[]>(initialSample || []);
  const [liveRowCount, setLiveRowCount] = useState(initialRowCount || 0);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (dfPath) {
      setSchemaLoading(true);
      api.post('/api/intelligence/df-schema', { filePath: dfPath })
        .then(res => {
          if (res.data.success) {
            setLiveSchema(res.data.data.schema || {});
            setLiveSample(res.data.data.sample || []);
            setLiveRowCount(res.data.data.row_count || initialRowCount || 0);
          }
        })
        .catch(() => {})
        .finally(() => setSchemaLoading(false));
    }
  }, [dfPath]);

  const handleSubmit = async () => {
    if (!input.trim() && !attachment) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: input.trim(),
      attachment: attachment ? { name: attachment.name, type: attachment.type } : undefined,
    };
    setMessages(prev => [...prev, userMsg]);
    const promptSummary = input.trim();
    setInput('');

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('message', promptSummary);
      formData.append('dfSchema', JSON.stringify(liveSchema));
      formData.append('dfSample', JSON.stringify(liveSample.slice(0, 20)));
      formData.append('history', JSON.stringify(messages.map(m => ({
        role: m.role,
        content: m.content,
      }))));

      if (attachment) {
        formData.append('image', attachment);
      }

      const response = await api.post('/api/custom-transform/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setAttachment(null);

      if (response.data.success) {
        const generated = response.data.data as GeneratedTransform;

        let friendlyContent = generated.explanation || 'Generated transformation code.';
        if (generated.fn_name === 'cannot_generate') {
          friendlyContent = `I'd love to help! But I need a specific transformation request to generate code for. Try describing what you want to do with your data — for example:\n\n\u2022 "Extract PO numbers from the description column"\n\u2022 "Split the amount column into currency and value"\n\u2022 "Normalize vendor names to uppercase"\n\u2022 "Flag rows where amount is negative"\n\u2022 "Create a running total by period"\n\nWhat would you like to do with your data?`;
        } else if (generated.fn_name === 'parse_error') {
          friendlyContent = 'Sorry, I had trouble understanding that request. Could you rephrase it with more specific details about what transformation you need?';
        }

        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: friendlyContent,
          generatedCode: generated,
        };
        setMessages(prev => [...prev, assistantMsg]);
        setReviewPromptSummary(promptSummary);
      } else {
        toast.error(response.data.error || 'Generation failed');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.error || 'Failed to generate transformation.',
          generatedCode: null,
        }]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Request failed');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Request failed. Please try again.',
        generatedCode: null,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(png|jpg|jpeg|webp|csv|xlsx)$/i)) {
      toast.error('Supported: PNG, JPG, WEBP, CSV, XLSX');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Maximum file size: 10MB');
      return;
    }

    setAttachment(file);
  };

  const handleReviewCode = (transform: GeneratedTransform) => {
    if (transform.fn_name === 'cannot_generate' || transform.fn_name === 'parse_error') return;
    setReviewingTransform(transform);
  };

  const handleTransformExecuted = (result: any) => {
    setReviewingTransform(null);
    if (result && onTransformApplied) {
      onTransformApplied(result);
    }
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: result?.status === 'success'
        ? `Transformation applied successfully. ${result.rows_after || 0} rows in result.`
        : `Transformation failed: ${result?.warnings?.[0] || 'Unknown error'}`,
      generatedCode: null,
    }]);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
              <Code2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-sm font-bold text-gray-800">Custom Transformation Studio</span>
              <p className="text-xs text-gray-500">
                {fileName} &middot; {liveRowCount.toLocaleString()} rows
                {Object.keys(liveSchema).length > 0 && ` \u00b7 ${Object.keys(liveSchema).length} columns`}
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={() => { setMessages([]); setReviewingTransform(null); }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin min-h-0">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Bot className="w-10 h-10 text-violet-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-600">Describe your transformation</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
              Explain what you need in plain English. Attach a screenshot of your data for better results.
              AI will generate Python code — you review and approve before anything runs.
            </p>
            {schemaLoading && (
              <p className="text-xs text-violet-500 mt-3 flex items-center justify-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading column schema...
              </p>
            )}
            {!schemaLoading && Object.keys(liveSchema).length > 0 && (
              <div className="mt-4 mx-auto max-w-md">
                <p className="text-xs font-semibold text-gray-500 mb-2">Available columns:</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {Object.entries(liveSchema).map(([col, info]: [string, any]) => (
                    <span key={col} className="px-2 py-0.5 text-[10px] font-mono bg-gray-100 text-gray-600 rounded-md border border-gray-200">
                      {col} <span className="text-gray-400">({info.dtype})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Bot className="w-4 h-4 text-violet-600" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${
              msg.role === 'user'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-50 border border-gray-200 text-gray-700'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              {msg.attachment && (
                <div className="mt-1.5 flex items-center gap-1 text-xs opacity-75">
                  {msg.attachment.type.startsWith('image') ? <Image className="w-3 h-3" /> : <FileSpreadsheet className="w-3 h-3" />}
                  {msg.attachment.name}
                </div>
              )}
              {msg.generatedCode && msg.generatedCode.fn_name !== 'cannot_generate' && msg.generatedCode.fn_name !== 'parse_error' && (
                <div className="mt-2 space-y-2">
                  {msg.generatedCode.warnings?.length > 0 && (
                    <div className="space-y-1">
                      {msg.generatedCode.warnings.map((w, j) => (
                        <p key={j} className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          {w}
                        </p>
                      ))}
                    </div>
                  )}
                  {msg.generatedCode.estimated_match_rates && Object.keys(msg.generatedCode.estimated_match_rates).length > 0 && (
                    <div className="space-y-1.5 mt-1">
                      {Object.entries(msg.generatedCode.estimated_match_rates).map(([col, rate]) => {
                        const rateStr = String(rate);
                        const numMatch = rateStr.match(/(\d+)/);
                        const pct = numMatch ? Math.min(parseInt(numMatch[1]), 100) : 0;
                        return (
                          <div key={col} className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-500 w-32 truncate font-mono" title={col}>{col}</span>
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5 min-w-[60px]">
                              <div className="bg-violet-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-500 w-10 text-right font-semibold">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReviewCode(msg.generatedCode!)}
                      className="px-3 py-1.5 text-xs font-bold bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      Review & Approve Code
                    </button>
                    <button
                      onClick={() => setInput(`The code needs revision: `)}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Revise
                    </button>
                  </div>
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-1">
                <User className="w-4 h-4 text-teal-600" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2 items-start">
            <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-violet-600" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                <span className="text-sm text-gray-500">Generating transformation code...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
        {attachment && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-violet-50 border border-violet-200 rounded-lg">
            {attachment.type.startsWith('image') ? <Image className="w-3.5 h-3.5 text-violet-500" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-violet-500" />}
            <span className="text-xs text-violet-700 flex-1 truncate">{attachment.name}</span>
            <button onClick={() => setAttachment(null)} className="text-violet-400 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".png,.jpg,.jpeg,.webp,.csv,.xlsx"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors flex-shrink-0"
            title="Attach screenshot or data sample"
          >
            <Paperclip className="w-4.5 h-4.5" />
          </button>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
            placeholder="Describe the transformation you need..."
            rows={1}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={loading || (!input.trim() && !attachment)}
            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
              loading || (!input.trim() && !attachment)
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                : 'bg-violet-600 text-white hover:bg-violet-700 shadow-sm'
            }`}
          >
            {loading ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Send className="w-4.5 h-4.5" />}
          </button>
        </div>
      </div>

      {reviewingTransform && (
        <CodeReviewPanel
          transform={reviewingTransform}
          rowCount={liveRowCount}
          dfPath={dfPath}
          sessionId={sessionId}
          engagementId={engagementId}
          promptSummary={reviewPromptSummary}
          onClose={() => setReviewingTransform(null)}
          onExecuted={handleTransformExecuted}
        />
      )}
    </div>
  );
};

export default CustomTransformChat;
